import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../config/constants';
import { Input, Select, Table, Tag, Spin, Alert, Card, Row, Col, Button } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';

interface Product {
  _id: string;
  modelName: string;
  status: string;
  fitCritic?: {
    kritik: string;
    fitImageUrl?: string;
  };
}

const { Option } = Select;

const FitKritikPage: React.FC = () => {
  const { hasPermission, isAdmin } = useContext(AuthContext);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fitKritikFilter, setFitKritikFilter] = useState<'all' | 'yapildi' | 'yapilmadi'>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const canView = isAdmin || hasPermission('fitcrt', 'read');

  useEffect(() => {
    if (!canView) return;
    fetchProducts();
    // eslint-disable-next-line
  }, [canView]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line
  }, [products, search, statusFilter, fitKritikFilter]);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/products`); // Tüm ürünleri çek
      if (!res.ok) throw new Error('Ürünler getirilemedi');
      const data = await res.json();
      setProducts(data);
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let tempProducts = [...products];

    if (search) {
      tempProducts = tempProducts.filter(p => p.modelName.toLowerCase().includes(search.toLowerCase()));
    }

    if (statusFilter) {
      tempProducts = tempProducts.filter(p => p.status === statusFilter);
    }

    if (fitKritikFilter === 'yapildi') {
      tempProducts = tempProducts.filter(p => p.fitCritic && p.fitCritic.kritik);
    } else if (fitKritikFilter === 'yapilmadi') {
      tempProducts = tempProducts.filter(p => !p.fitCritic || !p.fitCritic.kritik);
    }

    setFilteredProducts(tempProducts);
  };

  if (!canView) {
    return (
      <div className="p-8">
        <Alert message="Yetkisiz Erişim" description="Bu sayfayı görüntüleme yetkiniz yok." type="error" showIcon />
      </div>
    );
  }

  const columns = [
    {
      title: 'Fit Kritik Durumu',
      dataIndex: 'fitCritic',
      key: 'fitCritic',
      render: (fitCritic?: Product['fitCritic']) => {
        const hasKritik = fitCritic && fitCritic.kritik;
        return (
          <Tag icon={hasKritik ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
               color={hasKritik ? 'success' : 'error'}>
            {hasKritik ? 'Yapıldı' : 'Yapılmadı'}
          </Tag>
        );
      },
    },
    {
      title: 'Model Adı',
      dataIndex: 'modelName',
      key: 'modelName',
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag>{status.toUpperCase()}</Tag>,
    },
    {
      title: 'İşlem',
      key: 'action',
      render: (_: any, record: Product) => {
        const hasKritik = record.fitCritic && record.fitCritic.kritik;
        const targetPath = hasKritik ? `/fit/${record._id}` : `/fit/${record._id}/edit`;
        return (
          <Button type="primary" onClick={() => navigate(targetPath)}>
            {hasKritik ? 'Kritiği Görüntüle' : 'Fit Kritiği Yap'} 
          </Button>
        );
      },
    },
  ];

  return (
    <div className="p-4 md:p-8">
      <Card title="Fit Kritik Yönetimi" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Row gutter={[16, 16]} className="mb-6">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Model adı ile ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              value={statusFilter}
              onChange={value => setStatusFilter(value)}
              style={{ width: '100%' }}
              placeholder="Duruma göre filtrele"
              allowClear
            >
              <Option value="">Tüm Durumlar</Option>
              <Option value="draft">Taslak</Option>
              <Option value="active">Aktif</Option>
              <Option value="archived">Arşiv</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              value={fitKritikFilter}
              onChange={value => setFitKritikFilter(value as 'all' | 'yapildi' | 'yapilmadi')}
              style={{ width: '100%' }}
              placeholder="Fit Kritik Durumuna Göre Filtrele"
            >
              <Option value="all">Tümü</Option>
              <Option value="yapildi">Yapıldı</Option>
              <Option value="yapilmadi">Yapılmadı</Option>
            </Select>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-8">
            <Spin size="large" />
          </div>
        ) : error ? (
          <Alert message="Hata" description={error} type="error" showIcon />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredProducts}
            rowKey="_id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 'max-content' }}
            className="rounded-lg overflow-hidden"
          />
        )}
      </Card>
    </div>
  );
};

export default FitKritikPage; 